import React, { useCallback, useEffect, useRef } from 'react';
import { Button } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import { SYNC } from 'assets/icons';
import { useBodyModalOpen } from 'hooks';
import { hardReload, notification, VERSION_MISMATCH_EVENT } from 'utils/services';
import * as Styled from './styles';

const TOAST_ID = 'fe-version-mismatch';

// Put the prompt back when something else takes it away. notification.info() runs a blanket
// toast.dismiss() (utils/services/notification) and so does PageContainer's import reset, so
// the bell's "N unread messages" toast on the dashboard routinely swept this off screen a
// second after it appeared. checkVersionMismatch latches on `mismatchNotified` and dispatches
// VERSION_MISMATCH_EVENT at most once per page life, so nothing raised it again — the user was
// left on a stale bundle with no warning at all.
const HEAL_INTERVAL_MS = 20000;

// Non-dismissable on purpose: a stale bundle running against a new BE can hit
// breaking API contract changes, so silently letting the user dismiss the
// prompt and keep clicking around is exactly the failure mode we're trying
// to prevent. Manual reload is the only exit. notification.custom already
// sets autoClose: false; the rest overrides DEFAULT_NOTIFICATION defaults.
const TOAST_OPTIONS = {
  toastId: TOAST_ID,
  closeOnClick: false,
  closeButton: false,
};

function VersionWatcher() {
  // The one thing the heal needs to know: has the mismatch been reported at all? There is no
  // user-dismissal counterpart to DraftReminder's `dismissedRef` here — this prompt has no
  // close control, so once it is true it stays true for the life of the page.
  const mismatchedRef = useRef(false);

  // The toast container outranks the modal layer (100000 vs ui-kit/Modal's 9000), so a prompt
  // with no close button would paint over a dialog and cover its buttons. That was survivable
  // while the toast kept vanishing on its own; now that it comes back, it has to stand down
  // for dialogs or it would trap the user behind one.
  const isModalOpen = useBodyModalOpen();
  const isModalOpenRef = useRef(isModalOpen);
  isModalOpenRef.current = isModalOpen;

  const show = useCallback(() => {
    notification.custom(
      <Styled.Wrapper>
        <Styled.Title>A new version is available</Styled.Title>
        <Styled.Body>
          The app has been updated.
          <br />
          Hard reload to get the latest changes.
        </Styled.Body>
        <Styled.ButtonWrapper>
          <Button text="Hard reload" variant={BUTTON_TYPES.DANGER} icon={SYNC} onClick={hardReload} />
        </Styled.ButtonWrapper>
      </Styled.Wrapper>,
      TOAST_OPTIONS,
    );
  }, []);

  useEffect(() => {
    const handler = () => {
      mismatchedRef.current = true;
      if (isModalOpenRef.current) return;
      show();
    };
    window.addEventListener(VERSION_MISMATCH_EVENT, handler);
    return () => window.removeEventListener(VERSION_MISMATCH_EVENT, handler);
  }, [show]);

  // Hide for as long as a dialog is up, and bring it straight back when the dialog closes
  // rather than making the user wait out a heal tick.
  useEffect(() => {
    if (isModalOpen) {
      notification.dismiss(TOAST_ID);
    } else if (mismatchedRef.current) {
      show();
    }
  }, [isModalOpen, show]);

  // Pure in-memory bookkeeping — no IDB, no network, just "should this be on screen, and is it?".
  useEffect(() => {
    const interval = setInterval(() => {
      if (!mismatchedRef.current || isModalOpenRef.current) return;
      if (notification.isActive(TOAST_ID)) return;
      show();
    }, HEAL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [show]);

  return null;
}

export default VersionWatcher;
